import { useState } from 'react';

import { useAuth } from '../../providers/auth-provider';
import { useRoles } from '../../hooks/use-roles';
import type { RoleResponse } from '../../lib/api-client';

import { RoleEditor } from './RoleEditor';

const CAP_LABELS: Record<string, string> = {
  full_access: 'Full access',
  manage_users: 'Manage users',
  manage_roles: 'Manage roles',
  creator_mode: 'Creator Mode',
  sftp_access: 'SFTP access',
};

export function RolesView() {
  const { user } = useAuth();
  const { roles, status, error, createRole, updateRole, deleteRole } = useRoles();
  const [editing, setEditing] = useState<RoleResponse | null>(null);
  const [creating, setCreating] = useState(false);

  const canManage = user?.capabilities?.manage_roles === true || user?.capabilities?.full_access === true;

  return (
    <div style={{ padding: '20px 20px 24px', height: '100%', overflowY: 'auto', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Roles</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {roles.length} {roles.length === 1 ? 'role' : 'roles'}
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              background: 'var(--accent)',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 500,
              fontFamily: 'inherit',
            }}
          >
            + New role
          </button>
        )}
      </div>

      {status === 'loading' && <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading…</div>}
      {status === 'error' && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>{error ?? 'Failed to load roles.'}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {roles.map((r) => {
          const caps = Object.entries(r.capabilities).filter(([, v]) => v === true).map(([k]) => CAP_LABELS[k] ?? k);
          return (
            <div
              key={r.id}
              onClick={() => setEditing(r)}
              data-testid={`role-card-${r.slug}`}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: 14,
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.name}</span>
                {r.is_system && (
                  <span style={{ fontSize: 9, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: 0.4, background: 'var(--bg-alt)', padding: '2px 6px', borderRadius: 999, fontWeight: 500 }}>
                    system
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 8, minHeight: 14 }}>
                {r.description ?? ''}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {caps.length === 0 ? (
                  <span style={{ fontSize: 10, color: 'var(--text-mute)' }}>No capabilities</span>
                ) : (
                  caps.map((c) => (
                    <span key={c} style={{ fontSize: 10, color: 'var(--accent)', background: 'var(--accent-soft)', padding: '2px 7px', borderRadius: 999, fontWeight: 500 }}>
                      {c}
                    </span>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {(creating || editing) && (
        <RoleEditor
          role={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onCreate={async (body) => { await createRole(body); }}
          onUpdate={async (id, body) => { await updateRole(id, body); }}
          onDelete={canManage ? async (id) => { await deleteRole(id); } : undefined}
        />
      )}
    </div>
  );
}
